/* eslint-disable  */
import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import moment from 'moment';

import { safraConverter } from 'utils/converters';
import { appWarnings } from 'utils/appWarnings';
import { appendTipoEmissaoCachosToSafraPayload } from 'utils/marcacaoCachos';

import { useAuth } from 'hooks/auth';
import { useNewToast } from 'hooks/newToast';

const SafraContext = createContext({});

export const enrichSafraForSelect = (safra) => ({
  ...safra,
  productDescricao: safra?.product?.descricao || '',
});

export const SAFRAS_ADICIONAL_LABELS = [{ field: 'productDescricao' }];

export const SafraProvider = ({ children }) => {
  const { isAuth, token } = useAuth();
  const { showToast } = useNewToast();

  const [safras, setSafras] = useState([]);
  const [safrasFiltered, setSafrasFiltered] = useState([]);
  const [safrasLoading, setSafrasLoading] = useState(false);

  const [safra, setSafra] = useState({});
  const [safraLoading, setSafraLoading] = useState(false);

  const [insertSafraTutorialLoading, setInsertSafraTutorialLoading] =
    useState(false);
  const [insertSafraLoading, setInsertSafraLoading] = useState(false);
  const [safraAtual, setSafraAtual] = useState({});
  const [editSafraLoading, setEditSafraLoading] = useState(false);
  const [deleteSafraLoading, setDeleteSafraLoading] = useState(false);

  const getSafraAtual = useCallback(async () => {
    try {
      const response = await api.safras().getSafraAtual();
      setSafraAtual(response);
    } catch {}
  }, []);

  const getSafras = useCallback(async (filter) => {
    try {
      setSafrasLoading(true);
      const response = await api.safras().getSafras(filter);

      if (filter && (filter.search || filter.status !== undefined)) {
        setSafrasFiltered(response);
        const responseAll = await api.safras().getSafras();
        setSafras(responseAll);
      } else {
        setSafras(response);
        setSafrasFiltered(response);
      }

      setSafrasLoading(false);
    } catch (errors) {
      setSafrasLoading(false);
    }
  }, []);

  const getSafra = useCallback(async (id) => {
    try {
      setSafraLoading(true);
      const response = await api.safras().getSafra(id);
      setSafra(response);
      setSafraLoading(false);
    } catch (errors) {
      setSafraLoading(false);
    }
  }, []);

  const insertSafraTutorial = useCallback(
    async (data) => {
      try {
        setInsertSafraTutorialLoading(true);
        const { inicio, fim, produto } = data;

        const newData = {
          inicio,
          fim,
          produto: produto?.id || 1,
          descricao: safraConverter(data),
        };
        const response = await api.safras().insertSafraTutorial(newData);
        setSafras((old) => [...old, response]);
        setSafraAtual(response);
      } catch (err) {
        setInsertSafraTutorialLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const insertSafra = useCallback(
    async (data, filterDefault) => {
      try {
        setInsertSafraLoading(true);
        const { inicio, fim, talhoes, safraAtual, descricao } = data;
        const tempInicio = moment(inicio).format('YYYY-MM-DD');
        const tempFim = moment(fim).format('YYYY-MM-DD');

        let descricaoParaSalvar = descricao?.trim();
        if (!descricaoParaSalvar) {
          const anoInicio = moment(inicio).format('YYYY');
          const anoFim = moment(fim).format('YYYY');
          const nomeProduto = data?.produto.descricao.trim();
          descricaoParaSalvar = `SAFRA ${anoInicio}/${anoFim} - ${nomeProduto}`;
        }

        const newData = appendTipoEmissaoCachosToSafraPayload(
          {
            inicio: tempInicio,
            fim: tempFim,
            safraAtual: safraAtual ? 1 : 0,
            produto: data?.produto?.id,
            talhoes: talhoes
              ? talhoes?.filter((item) => item.selecionado === 1)
              : [],
            descricao: descricaoParaSalvar,
          },
          data,
        );
        const response = await api.safras().insertSafra(newData);
        getSafras(filterDefault);
        showToast({
          type: 'success',
          message: 'Safra adicionada com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setInsertSafraLoading(false);
      }
    },
    [showToast, getSafras],
  );

  const editSafra = useCallback(
    async (id, data, filterDefault, options = {}) => {
      try {
        setEditSafraLoading(true);
        const { inicio, fim, talhoes, safraAtual, descricao } = data;

        const tempInicio = moment(inicio).format('YYYY-MM-DD');
        const tempFim = moment(fim).format('YYYY-MM-DD');

        let descricaoParaSalvar = descricao?.trim();
        if (!descricaoParaSalvar) {
          const anoInicio = moment(inicio).format('YYYY');
          const anoFim = moment(fim).format('YYYY');
          const nomeProduto = data?.produto.descricao.trim();
          descricaoParaSalvar = `SAFRA ${anoInicio}/${anoFim} - ${nomeProduto}`;
        }

        const newData = appendTipoEmissaoCachosToSafraPayload(
          {
            inicio: tempInicio,
            fim: tempFim,
            safraAtual: safraAtual ? 1 : 0,
            produto: data?.produto?.id,
            talhoes: talhoes
              ? talhoes?.filter((item) => item.selecionado === 1)
              : [],
            descricao: descricaoParaSalvar,
          },
          data,
          {
            tipoEmissaoAtual: options.tipoEmissaoAtual,
            isUpdate: true,
          },
        );
        const response = await api.safras().editSafra(id, newData);

        showToast({
          type: 'success',
          message: 'Safra editada com sucesso!',
        });

        getSafras(filterDefault);

        if (data?.safraAtual) {
          setSafraAtual(response);
        }
        setEditSafraLoading(false);
      } catch (err) {
        setEditSafraLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getSafras],
  );

  const deleteSafra = useCallback(
    async (id, filterDefault) => {
      try {
        setDeleteSafraLoading(true);
        await api.safras().deleteSafra(id);
        getSafras(filterDefault);
        showToast({
          type: 'success',
          message: 'Safra removida com sucesso!',
        });
        setDeleteSafraLoading(false);
      } catch (err) {
        setDeleteSafraLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getSafras],
  );

  const toggleStatusSafra = useCallback(
    async (id, filter) => {
      try {
        await api.safras().toogleStatusSafra(id);
        getSafras(filter);
        showToast({
          type: 'success',
          message: 'Status da safra alterado com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getSafras],
  );

  const init = useCallback(async () => {
    getSafras();
    getSafraAtual();
  }, [getSafras, getSafraAtual]);

  const down = useCallback(async () => {
    setSafras([]);
    setSafrasFiltered([]);
    setSafraAtual({});
  }, []);

  const safrasAgrupadas = useMemo(() => {
    const ativas = safras
      .filter((s) => s.ativa === true || s.ativa === 1)
      .map(enrichSafraForSelect);
    const inativas = safras
      .filter((s) => s.ativa === false || s.ativa === 0)
      .map(enrichSafraForSelect);
    const grupos = [];
    if (ativas.length > 0) {
      grupos.push({ label: 'Safras Ativas', options: ativas });
    }
    if (inativas.length > 0) {
      grupos.push({ label: 'Safras Inativas', options: inativas });
    }
    return grupos;
  }, [safras]);

  React.useEffect(() => {
    if (!!isAuth && token !== 'initial') {
      init();
    } else {
      down();
    }
  }, [isAuth, init, down, token]);

  return (
    <SafraContext.Provider
      value={{
        getSafras,
        safrasFiltered,
        safras,
        safrasAgrupadas,
        safrasLoading,
        insertSafraTutorialLoading,
        insertSafraTutorial,
        insertSafra,
        insertSafraLoading,
        editSafraLoading,
        editSafra,
        deleteSafraLoading,
        deleteSafra,
        getSafra,
        safra,
        safraLoading,
        setSafraAtual,
        safraAtual,
        getSafraAtual,
        toggleStatusSafra,
      }}
    >
      {children}
    </SafraContext.Provider>
  );
};

export function useSafra() {
  const context = useContext(SafraContext);

  if (!context) throw new Error('useSafra must be used within an AuthProvider');

  return context;
}
